/**
 * CN-0015: Plan Management Mode Types
 *
 * Three app modes for the plot plan editor:
 * - planning: create/delete objects, resize grid (editor)
 * - planting: add plantings to beds, bulk placement
 * - management: view details, assign/execute tasks, bulk actions
 */

/** App-level mode for the plan editor */
export type PlanAppMode = 'planning' | 'planting' | 'management';

/** Visibility flags for optional plan overlays */
export interface PlanLayerVisibility {
  plantings: boolean;
  watering: boolean;
  issues: boolean;
}

/** Editor sub-mode (only relevant in planning mode) */
export type PlanEditorMode = 'navigate' | 'paint' | 'erase' | 'bed' | 'plot' | 'delete-bed' | 'delete-plot';

/** Management sub-mode */
export type ManagementMode = 'select' | 'multi-select';

/** Bed info enriched with planting data for management mode */
export interface BedPlantingInfo {
  bedId: string;
  bedName?: string;
  area?: number;
  plantings: BedPlantingItem[];
}

/** Single planting on a bed (from grid-view or plantings API) */
export interface BedPlantingItem {
  id: string;
  cropId: string; // CN-0012: UUID-only
  cropName: string;
  cropLatinName: string;
  lifecycleStatus: string;
  lifecyclePhase?: string | null;
  lifecycleGateProgressPct?: number | null;
  lifecycleNextPhase?: string | null;
  lifecycleYieldMultiplier?: number | null;
  plantingType?: 'single' | 'batch';
  quantity?: number;
  autoHarvest?: boolean;
}

/** Action context for the management panel — what actions are available */
export interface ManagementActionContext {
  /** Selected bed ID */
  bedId: string;
  /** Planting on the bed (if any) */
  planting: BedPlantingItem | null;
  /** Whether the bed is empty (no active plantings) */
  isEmpty: boolean;
  /** Bed has a blocking non-active planting that still occupies the bed */
  hasBlockingPlanting: boolean;
  /** The blocking planting item (if any) */
  blockingPlanting: BedPlantingItem | null;
  /** Available action categories */
  availableActions: ManagementActionType[];
}

// ─── Planning Mode Selection ────────────────────────────────────────────────

/** Analysis of cells selected in planning mode (select-then-act) */
export interface PlanningSelectionInfo {
  /** All selected cell keys (including expanded bed cells) */
  selectedCellKeys: Set<string>;
  /** Original selection rectangle bounds */
  rectMinRow: number;
  rectMaxRow: number;
  rectMinCol: number;
  rectMaxCol: number;
  /** Content analysis */
  emptyCellCount: number;
  pathCellCount: number;
  obstacleCellCount: number;
  structuralObjectCellCount: number;
  /**
   * Selected cells under non-covering structures (fences, low fences, outbuildings,
   * lamp posts). Beds and paths cannot be placed over them; coverings are fine.
   */
  nonCoveringStructuralObjectCellCount: number;
  /** Selected cells that cross a structural object's outer boundary */
  structuralObjectBoundaryCrossingCellCount: number;
  structuralObjectIds: string[];
  /** Из structuralObjectIds — заборы и изгороди: в них можно врезать калитку. */
  fenceStructuralObjectIds: string[];
  beds: Array<{ bedId: string; hasPlanting: boolean; cellCount: number }>;
  /** True when selection contains any beds with active plantings */
  hasPlantedBeds: boolean;
  /** True when selection contains only empty cells (no beds, paths, obstacles) */
  allEmpty: boolean;
  /** True when selection contains at least one entity (bed, path, or obstacle) */
  hasEntities: boolean;
  /** Total number of selected cells */
  totalCells: number;
}

/** Action types available in planning mode after selection */
export type PlanningActionType =
  | 'create-bed'
  | 'fill-path'
  | 'greenhouse'
  | 'outbuilding'
  | 'solid-fence'
  | 'low-fence'
  | 'gate'
  | 'double-gate'
  | 'lamp-post'
  | 'delete';

// ─── Management Mode ────────────────────────────────────────────────────────

/** Types of actions available in management mode */
export type ManagementActionType =
  | 'view-bed'         // navigate to bed detail page
  | 'view-planting'    // navigate to planting detail page
  | 'add-planting'     // plant on empty bed
  | 'bed-action'       // event/task for the bed (watering, soil work, etc.)
  | 'planting-action'  // event/task for the planting (pruning, measurement, etc.)
  | 'batch-action';    // action on multiple selected entities

const BLOCKING_STATUSES = new Set(['dead']);

/** Build action context from bed+planting info */
export function buildActionContext(bedInfo: BedPlantingInfo): ManagementActionContext {
  const activePlanting = bedInfo.plantings.find(
    (p) => p.lifecycleStatus === 'active'
  ) ?? null;

  const isEmpty = bedInfo.plantings.filter((p) => p.lifecycleStatus === 'active').length === 0;

  // A non-active planting that physically occupies the bed blocks new planting
  const blockingPlanting = isEmpty
    ? (bedInfo.plantings.find((p) => BLOCKING_STATUSES.has(p.lifecycleStatus)) ?? null)
    : null;
  const hasBlockingPlanting = blockingPlanting !== null;

  const availableActions: ManagementActionType[] = ['view-bed'];

  if (isEmpty && !hasBlockingPlanting) {
    availableActions.push('add-planting');
  }

  // Bed-level actions are always available
  availableActions.push('bed-action');

  if (activePlanting) {
    availableActions.push('view-planting', 'planting-action');
  }

  return {
    bedId: bedInfo.bedId,
    planting: activePlanting,
    isEmpty,
    hasBlockingPlanting,
    blockingPlanting,
    availableActions,
  };
}
